import confettiLottie from 'base/ui/confetti.lottie.json';
import classNames from 'classnames';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { createPortal } from 'react-dom';
import { Transition, TransitionGroup } from 'react-transition-group';
import { keyOfObject } from '../helpers';
import { Button } from './button';
import { LottiePlayer } from './lottie';
import styles from './toast.css';
import { TextSmall } from './typography';

type PersistentToastRequest = Readonly<{
  label: string;
  theme?: 'dark' | 'light';
  confetti?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  customLeft?: React.ReactNode;
  customRight?: React.ReactNode;
}>;
type ToastRequest = Readonly<
  PersistentToastRequest & {
    group?: symbol;
    duration?: number;
  }
>;
const DEFAULT_THEME = 'dark' as const;
const DEFAULT_DURATION = 3_000;
export const LONG_TOAST_DURATION = 5_000;
export const EXTRA_LONG_TOAST_DURATION = 10_000;

export class ToastQueue {
  readonly queue = observable.array<ToastRequest>([], { deep: false });
  readonly persistent = observable.array<
    () => PersistentToastRequest | undefined
  >([], {
    deep: false,
  });

  constructor() {}

  push(req: ToastRequest) {
    const reqClone = { ...req }; // deletion requires each request have a unique identity
    if (req.group) {
      this.queue.replace(this.queue.filter((t) => t.group !== req.group));
    }
    this.queue.push(reqClone);
    return () => {
      this.queue.remove(reqClone);
    };
  }

  addPersistent(getToastReq: () => PersistentToastRequest | undefined) {
    this.persistent.push(getToastReq);
    return () => void this.persistent.remove(getToastReq);
  }

  get top(): ToastRequest | undefined {
    return this.queue[0];
  }

  pop(toast: ToastRequest) {
    if (this.top === toast) this.queue.shift();
  }
}

export const ToastLayer = observer(function _ToastLayer({
  queue,
  bottomOffset = 0,
}: {
  queue: ToastQueue;
  bottomOffset?: number;
}) {
  const top = queue.top;
  // We should measure them, but this works for now:
  const toastHeight = 56; // px

  React.useEffect(() => {
    if (!top) return;
    const timer = setTimeout(
      () => queue.pop(top),
      top.duration ?? DEFAULT_DURATION,
    );
    return () => clearTimeout(timer);
  }, [top]);

  const toasts: { req: ToastRequest; key: string }[] = [];
  if (top) toasts.push({ req: top, key: keyOfObject(top) });
  for (const getter of queue.persistent) {
    const req = getter();
    if (req) toasts.push({ req, key: keyOfObject(getter) });
  }
  toasts.reverse();

  return (
    <TransitionGroup>
      {toasts.map(({ req, key }, i) => (
        <Transition key={key} timeout={300}>
          {(state) =>
            createPortal(
              <div
                className={classNames(styles.layer, styles[state])}
                style={{
                  ['--toastBottomOffset' as string]:
                    bottomOffset + i * toastHeight + 'px',
                }}
              >
                <ToastNotification {...req} />
              </div>,
              document.body,
            )
          }
        </Transition>
      ))}
    </TransitionGroup>
  );
});

export function ToastNotification({
  customLeft,
  label,
  confetti,
  action,
  customRight,
  theme = DEFAULT_THEME,
}: Omit<ToastRequest, 'duration'>) {
  return (
    <div className={classNames(styles.toast, styles['theme-' + theme])}>
      {customLeft}
      <div className={styles.text}>
        <TextSmall>{label}</TextSmall>
      </div>
      {action && <Button {...action} variant="primary" />}
      {customRight}
      {confetti && <ConfettiAnimation />}
    </div>
  );
}

function ConfettiAnimation() {
  const portal = createPortal(
    <div className={styles.confetti}>
      <LottiePlayer animationData={confettiLottie} loop={false} delayMs={250} />
    </div>,
    document.body,
  );
  return <>{portal}</>;
}
